import { Component, OnDestroy, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { userActions } from 'src/app/models/enums/user-permissions.enum';
import { SubHeaderData } from 'src/app/models/sub-header-data';
import { ManagementApiService, UserUtilityService } from 'src/app/services';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { InstanceSettingModel } from '../../models/informate/instance-setting-model';

@Component({
    selector: 'app-settings-cdms',
    templateUrl: './settings-cdms.component.html',
    styleUrls: ['./settings-cdms.component.scss'],
    standalone: false,
})
export class SettingsCdmsComponent implements OnInit, OnDestroy {
    @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

    generalTabIndex: any;
    subHeaderData: SubHeaderData;
    cdmsFormGroup: UntypedFormGroup;
    destroy$: Subject<any>;
    viewDatabaseLookupTab: boolean;
    viewGradeMatrixTab: boolean;
    profileSettingAreaId: string;
    userSettings: InstanceSettingModel[];
    defaultUnitSettings: InstanceSettingModel[];
    cdmsSettingsForm: UntypedFormGroup;
    viewCertificatesTab: boolean;
    lookupTabTitle = 'DB Lookup';

    constructor(
        private formBuilder: UntypedFormBuilder,
        private mgmtApi: ManagementApiService,
        private userUtilityService: UserUtilityService,
        private fb: UntypedFormBuilder,
        private cd: ChangeDetectorRef,
        private user_utility: UserUtilityService,
        private customToastService: CustomToastService,
        private router: Router,
        private route: ActivatedRoute,
        private globalEmittersService: GlobalEmittersService,
        private translocoService: TranslocoService
    ) {
        this.destroy$ = new Subject();
        this.viewDatabaseLookupTab = false;
        this.viewGradeMatrixTab = false;
    }

    async ngOnInit(): Promise<void> {
        this.globalEmittersService.RuleSaveDisable = false;
        this.subHeaderData = this.setSubHeaderValues();

        // Check user permissions for CDMS settings tabs
        this.viewDatabaseLookupTab = await this.userUtilityService.checkUserPermissionDataAndRoute('cdms-settings-lookups', userActions.View);
        this.viewGradeMatrixTab = await this.userUtilityService.checkUserPermissionDataAndRoute('cdms-settings-grade-matrix', userActions.View);
        this.viewCertificatesTab = await this.userUtilityService.checkUserPermissionDataAndRoute('cdms-settings-certificates', userActions.View);
        this.setLookupTabTitle();

        this.initializeFormGroup();
        await this.checkUserAccessLevel();
        // this.getSettings();
    }

    setSubHeaderValues(): SubHeaderData {
        let headerData = new SubHeaderData();
        headerData.displayText = this.translocoService.translate('SETTING_PAGE.SETTING_SUB_HEADER.Settings');
        headerData.disableActions = true;
        headerData.defaultImageSrc = '../../../../../../assets/media/logos/settings-black.png';
        headerData.mouseOverImageSrc = '../../../../../../assets/media/logos/settings-yellow.png';
        headerData.isCdmsSettings = true;
        headerData.isGeneralSettings = false;
        headerData.isInstanceSettings = false;
        return headerData;
    }

    // Check user access levels
    async checkUserAccessLevel(): Promise<void> {
        this.viewDatabaseLookupTab = await this.userUtilityService.checkUserPermissionDataAndRoute('cdms-settings-lookups', userActions.View);
        this.viewGradeMatrixTab = await this.userUtilityService.checkUserPermissionDataAndRoute('cdms-settings-grade-matrix', userActions.View);
        this.viewCertificatesTab = await this.userUtilityService.checkUserPermissionDataAndRoute('cdms-settings-certificates', userActions.View);
        this.setLookupTabTitle();
    }

    private setLookupTabTitle(): void {
        if (this.viewDatabaseLookupTab && this.viewGradeMatrixTab) {
            this.lookupTabTitle = 'DB Lookup and Grade Matrix';
        } else if (this.viewDatabaseLookupTab && !this.viewGradeMatrixTab) {
            this.lookupTabTitle = 'DB Lookup';
        } else if (!this.viewDatabaseLookupTab && this.viewGradeMatrixTab) {
            this.lookupTabTitle = 'Grade Matrix';
        } else {
            this.lookupTabTitle = 'DB Lookup';
        }
    }

    // Initialization of parent formGroup
    initializeFormGroup(): void {
        this.cdmsFormGroup = this.formBuilder.group({
            databaseLookup: new UntypedFormArray([]),
            gradeMatrix: new UntypedFormArray([]),
        });
    }

    getSettings(): void {
        this.mgmtApi.getSettingProfileAreaId('cdms').subscribe((res) => {
            if (res.success === true) {
                this.profileSettingAreaId = res.response.settingAreaId;
                if (this.profileSettingAreaId) {
                    this.mgmtApi.getSettingbySettinfAreaIdForIdms(this.profileSettingAreaId).subscribe((userSettings) => {
                        if (userSettings.success === true) {
                            this.userSettings = userSettings.response;
                            this.buildForm();
                            this.defaultUnitSettings = this.getSectionData('Defaults', false);
                            this.cd.markForCheck();
                        } else {
                            this.user_utility.errorModelPopup(userSettings.message);
                        }
                    });
                }
            } else {
                this.user_utility.errorModelPopup(res.message);
            }
        });
    }

    buildForm(): void {
        this.cdmsSettingsForm = null;
        this.cdmsSettingsForm = this.fb.group({});

        this.userSettings.forEach((obj, index) => {
            const validatorsToAdd = [];

            if (obj.setting.isRequired) {
                validatorsToAdd.push(Validators.required);
            }

            this.cdmsSettingsForm.addControl(obj.setting.key, this.fb.control(this.getFormStateValue(obj), validatorsToAdd));
        });
    }

    getSectionData(sectionName: string, isSubSection: boolean = false): InstanceSettingModel[] {
        const sections: InstanceSettingModel[] = this.userSettings.filter((item: InstanceSettingModel) => {
            if (isSubSection) {
                return item.setting.settingSubSection?.name.toLowerCase() === sectionName.toLowerCase();
            }

            return item.setting.settingSubSection?.settingSection.name.toLowerCase() === sectionName.toLowerCase();
        });

        return sections;
    }

    getFormStateValue(instanceSetting: InstanceSettingModel) {
        if (instanceSetting.value) {
            return instanceSetting.value;
        }

        if (instanceSetting.settingValue) {
            return instanceSetting.settingValue.settingValueId;
        }

        return null;
    }

    tabChanged(event: any): void {
        this.generalTabIndex = this.tabGroup._tabs.find((tab) => tab.textLabel === event.tab.textLabel).ariaLabel;
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
